import { useMemo } from 'react';

import { FeatureFlag } from '@revelio/core';
import {
  type Dimension1,
  EntKind,
  MapCoordsGranularity,
  POSTING_GET_DATA,
  POSTINGS_V3_GET_DATA,
  type PostingsDataQuery,
  type PostingsV3DataQuery,
  type PostingsV3Ent,
} from '@revelio/data-access';

import { useSelectionListsForKeys } from '../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { getDim1 } from '../../filters/utils/getDim1';
import { getPostingFilters } from '../../filters/utils/getPostingFilters';
import { buildPostingsV3VariablesFromEntities } from '../../filters/utils/getPostingsV3Variables';
import { useChart } from '../Chart/ChartProvider';
import type { MapPoint } from '../Chart/Map/types';
import { determineLocationType } from '../Chart/Map/utils/location-type-utils';
import type { PostingMetricValues } from '../PageConfigProvider/types/metric';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import { convertPostingsV3MapData } from './utils/convert-postings-v3-map-data';
import {
  extractGranularitiesWithIds,
  type GranularityWithIds,
  useCoordinateResolverForMixedGranularities,
} from './utils/coordinate-resolver';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from './utils/data-provider-results-utils';

type PostingMapDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  metric: PostingMetricValues;
  exchangeRate?: number;
  children: DataProviderChildren<{ points: MapPoint[] } | undefined>;
};

const mapMetricToField = (metric: PostingMetricValues) => {
  const mapping: Partial<
    Record<
      PostingMetricValues,
      'salary' | 'time_to_fill' | 'active' | 'new' | 'removed'
    >
  > = {
    posting_salary: 'salary',
    time_to_fill: 'time_to_fill',
    postings_active: 'active',
    postings_volume: 'active',
    postings_new: 'new',
    postings_removed: 'removed',
  };
  return mapping[metric] ?? 'active';
};

export const PostingMapDataProvider = (props: PostingMapDataProviderProps) => {
  const isPostingsV3Enabled = useUnleashFlag(FeatureFlag.PostingsV3);

  if (isPostingsV3Enabled) {
    return <PostingMapDataProviderV3 {...props} />;
  }

  return <PostingMapDataProviderV2 {...props} />;
};

const PostingMapDataProviderV2 = ({
  primaryDimension,
  metric,
  exchangeRate,
  children,
}: PostingMapDataProviderProps) => {
  const { item: _item } = useChart();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'week' });
  const { selectionLists } = useSelectionListsForKeys(['country', 'msa']);
  const { country: countryList, msa: msaList } = selectionLists;
  const postingFilters = getPostingFilters(primaryDimension, effectiveFilters);

  const isGeographyView = !!getDim1(FilterId.GEOGRAPHY);

  // Create filter configurations for dual requests when countries are selected
  const { countryFilters, msaFilters, needsDualRequest } = useMemo(() => {
    // Wait until both universe lists have loaded before enriching — they
    // arrive together from one geo-tree fetch, and enriching with a half-loaded
    // universe would send the MSA query with an empty id list.
    if (!countryList || !msaList) {
      return {
        countryFilters: {
          filters: postingFilters,
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        msaFilters: null,
        needsDualRequest: false,
      };
    }

    const { region, country, msa } = postingFilters;

    // If no geo filters are selected, use all countries
    if (!region?.length && !country?.length && !msa?.length) {
      const enrichedCountry = countryList.map((c) => Number(c.id));
      return {
        countryFilters: {
          filters: {
            ...postingFilters,
            region: [],
            country: enrichedCountry,
            msa: [],
          },
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        msaFilters: null,
        needsDualRequest: false,
      };
    }
    // If only countries are selected, we need dual requests
    else if (country?.length && !msa?.length) {
      const enrichedMsa = (msaList ?? [])
        .filter((m) => country.includes(Number(m.country)))
        .map((m) => Number(m.id));

      return {
        countryFilters: {
          filters: {
            ...postingFilters,
            region: [],
            country: country,
            msa: [],
          },
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        msaFilters: {
          filters: {
            ...postingFilters,
            region: [],
            country: [],
            msa: enrichedMsa,
          },
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        needsDualRequest: true,
      };
    }
    // For other cases (regions or MSAs selected), use single request
    else {
      return {
        countryFilters: {
          filters: postingFilters,
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        msaFilters: null,
        needsDualRequest: false,
      };
    }
  }, [postingFilters, countryList, msaList]);

  // First request (always made)
  const [{ data: countryData, status: countryStatus }] =
    useUrqlQueryWithStatus<PostingsDataQuery>({
      query: POSTING_GET_DATA,
      variables: countryFilters,
      pause: !isGeographyView,
    });

  // Second request (only when dual request is needed)
  const [{ data: msaData, status: msaStatus }] =
    useUrqlQueryWithStatus<PostingsDataQuery>({
      query: POSTING_GET_DATA,
      variables: msaFilters || undefined,
      pause: !needsDualRequest || !msaFilters || !isGeographyView,
    });

  // Combine status and data from both requests
  const status = needsDualRequest
    ? combineQueryStatus([countryStatus, msaStatus])
    : countryStatus;

  const data = useMemo(() => {
    if (!needsDualRequest) {
      return countryData;
    }

    // Merge data from both requests
    if (!countryData?.posting || !msaData?.posting) {
      return countryData || msaData;
    }

    // Combine entities from both requests
    const combinedEntities = [...countryData.posting, ...msaData.posting];

    return {
      ...countryData,
      posting: combinedEntities,
    };
  }, [countryData, msaData, needsDualRequest]);

  const entities = useMemo(() => data?.posting ?? [], [data?.posting]);

  const metricField = mapMetricToField(metric);

  const geoData = useMemo(() => {
    return entities
      .map((entity) => {
        const id = Number(entity?.metadata?.id ?? 0);
        const label =
          entity?.metadata?.shortName || entity?.metadata?.longName || '';
        const last = entity?.category?.[entity?.category.length - 1];
        const rawValue = last?.metrics?.[metricField] ?? null;
        if (!id || rawValue == null) return null;
        const value =
          metric === 'posting_salary' && exchangeRate
            ? Number(rawValue) * exchangeRate
            : Number(rawValue);
        return { id, label, value };
      })
      .filter(
        (d): d is { id: number; label: string; value: number } => d !== null
      );
  }, [entities, metricField, metric, exchangeRate]);

  const granularitiesWithIds = useMemo(() => {
    return extractGranularitiesWithIds(geoData);
  }, [geoData]);

  const { data: coords, loading: coordsLoading } =
    useCoordinateResolverForMixedGranularities(granularitiesWithIds);

  const points = useMemo((): MapPoint[] => {
    if (!coords.length || !geoData.length) return [];
    const coordMap = new Map(coords.map((c) => [c.id, c] as const));
    return geoData
      .map((d) => {
        const c = coordMap.get(d.id);
        if (!c) return null;
        const locationType = determineLocationType(c.name, c.granularity);
        return {
          id: String(d.id),
          label: d.label || c.name,
          lat: c.lat,
          lon: c.lon,
          value: d.value,
          circleRadius: 0,
          locationType,
        } as MapPoint;
      })
      .filter((p): p is MapPoint => p !== null);
  }, [coords, geoData]);

  const finalStatus = coordsLoading
    ? DataProviderQueryStatus.Fetching
    : (status ?? DataProviderQueryStatus.Idle);

  return children({ data: { points }, status: finalStatus });
};

const granularityToEntKind = (granularity: MapCoordsGranularity): EntKind => {
  switch (granularity) {
    case MapCoordsGranularity.Country:
      return EntKind.Country;
    case MapCoordsGranularity.Msa:
      return EntKind.Msa;
    case MapCoordsGranularity.Region:
      return EntKind.Region;
  }
};

const PostingMapDataProviderV3 = ({
  primaryDimension,
  metric,
  exchangeRate,
  children,
}: PostingMapDataProviderProps) => {
  const { item: _item } = useChart();
  const { activeFilterMap } = useFilterContext();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'week' });
  const { selectionLists } = useSelectionListsForKeys(['country', 'msa']);
  const { country: countryList, msa: msaList } = selectionLists;
  const postingFilters = getPostingFilters(primaryDimension, effectiveFilters);

  const isGeographyView = !!getDim1(FilterId.GEOGRAPHY);

  // Geographies to plot: no geo filter → all countries; only countries → those
  // countries + their MSAs; regions/MSAs → as selected. These become
  // `req.entities` of a single request.
  const geoGranularities = useMemo((): GranularityWithIds[] => {
    // Wait for both universe lists — they load together; enriching with a
    // half-loaded universe would request an empty geography set.
    if (!countryList || !msaList) return [];

    // `PostingFilters` geo ids are loosely typed; narrow to numbers.
    const toNumberIds = (
      values: ReadonlyArray<string | number | null> | undefined | null
    ): number[] =>
      (values ?? [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

    const region = toNumberIds(postingFilters.region);
    const country = toNumberIds(postingFilters.country);
    const msa = toNumberIds(postingFilters.msa);

    if (!region.length && !country.length && !msa.length) {
      return [
        {
          granularity: MapCoordsGranularity.Country,
          ids: countryList.map((c) => Number(c.id)),
        },
      ];
    }

    if (country.length && !msa.length) {
      const enrichedMsa = msaList
        .filter((m) => country.includes(Number(m.country)))
        .map((m) => Number(m.id));
      return [
        { granularity: MapCoordsGranularity.Country, ids: country },
        { granularity: MapCoordsGranularity.Msa, ids: enrichedMsa },
      ];
    }

    const granularities: GranularityWithIds[] = [];
    if (region.length) {
      granularities.push({
        granularity: MapCoordsGranularity.Region,
        ids: region,
      });
    }
    if (country.length) {
      granularities.push({
        granularity: MapCoordsGranularity.Country,
        ids: country,
      });
    }
    if (msa.length) {
      granularities.push({ granularity: MapCoordsGranularity.Msa, ids: msa });
    }
    return granularities;
  }, [postingFilters, countryList, msaList]);

  // One request entity per geography; the converter zips the response back to
  // this list by index to recover each geo's id for the coord lookup.
  const requestEntities = useMemo(
    (): PostingsV3Ent[] =>
      geoGranularities.flatMap((g) =>
        g.ids.map((id) => ({
          EntID: id,
          EntType: granularityToEntKind(g.granularity),
        }))
      ),
    [geoGranularities]
  );

  const variables = useMemo(
    () =>
      buildPostingsV3VariablesFromEntities({
        entities: requestEntities,
        effectiveFilters,
        defaultMetricMode: 'posting',
        activeFilterMap,
        // Pass the prop explicitly so the request's dimension matches the
        // geo-enrichment above instead of relying on entity-kind derivation.
        primaryDimension,
      }),
    [requestEntities, effectiveFilters, activeFilterMap, primaryDimension]
  );

  const [{ data, status }] = useUrqlQueryWithStatus<PostingsV3DataQuery>({
    query: POSTINGS_V3_GET_DATA,
    variables,
    pause: !isGeographyView || requestEntities.length === 0,
  });

  const { data: coords, loading: coordsLoading } =
    useCoordinateResolverForMixedGranularities(geoGranularities);

  const points = useMemo(
    (): MapPoint[] =>
      convertPostingsV3MapData({
        data,
        requestEntities,
        metric,
        exchangeRate,
        coords,
      }),
    [data, requestEntities, metric, exchangeRate, coords]
  );

  const finalStatus = coordsLoading
    ? DataProviderQueryStatus.Fetching
    : (status ?? DataProviderQueryStatus.Idle);

  return children({ data: { points }, status: finalStatus });
};
